import { parseJSON } from 'js/tools';
import patterns from 'js/validationPatterns';

const validator = {
    /**
     * Initialize Field interactions
     * @param field
     */
    initFieldValidation: field => {
        const validationConfig = JSON.parse(field.getAttribute('data-validation-config'));
        const input = field.querySelector('input');
        const { events, rules, debounceTimeout } = validationConfig;
        const mode = validationConfig.mode || 'sync';

        if (input === null) {
            return;
        }

        if (!Array.isArray(events) || events.length === 0) {
            return;
        }

        if (!Array.isArray(rules) || rules.length === 0) {
            return;
        }

        let debounceTimer;
        let debounceDelay = debounceTimeout || 3500;

        events.forEach(event => {
            input.addEventListener(event, e => {
                const eventType = e.type;
                const prevState = field.getAttribute('data-state');
                const validate = mode === 'async' ? validator.validateFieldAsync : validator.validateField;

                if (eventType === 'keyup' || eventType === 'input') {
                    if (typeof debounceTimer !== 'undefined') {
                        clearInterval(debounceTimer);
                    }

                    if (prevState === 'error' || prevState === 'success') {
                        debounceDelay = debounceTimeout > 500 ? 500 : debounceTimeout;
                    }

                    debounceTimer = setTimeout(() => {
                        validate(field);
                    }, debounceDelay);
                } else {
                    if (typeof debounceTimer !== 'undefined') {
                        clearInterval(debounceTimer);
                    }

                    validate(field);
                }
            });
        });
    },

    /**
     * Validate all fields in form
     * @param form
     * @return Validation report object: {{isValid: boolean, validFields: Array, invalidFields: Array}}
     */
    validateForm: async form => {
        const fieldsToValidate = form.querySelectorAll('[data-validation-config]');
        const formValidationReport = {
            isValid: true,
            validFields: [],
            invalidFields: []
        };

        for (let i = 0; i < fieldsToValidate.length; i++) {
            const field = fieldsToValidate[i];
            const validationConfig = JSON.parse(field.getAttribute('data-validation-config'));
            const input = field.querySelector('input');
            const { rules } = validationConfig;
            const mode = validationConfig.mode || 'sync';

            if (input !== null && Array.isArray(rules) && rules.length > 0) {
                const fieldValidationReport =
                    /* eslint-disable-next-line no-await-in-loop */
                    mode === 'async' ? await validator.validateFieldAsync(field) : validator.validateField(field);

                if (fieldValidationReport.isValid) {
                    formValidationReport.validFields.push(fieldValidationReport);
                } else {
                    formValidationReport.isValid = false;
                    formValidationReport.invalidFields.push(fieldValidationReport);
                }
            }
        }

        return formValidationReport;
    },

    /**
     * Process a validation report generated
     * @param field
     * @param validationReport
     */
    processFieldReport: (field, validationReport) => {
        const { validationConfig, isValid } = validationReport;
        const messages = field.querySelector('.input-messages');
        const isInitialState = validationReport.validRules.length === 0 && validationReport.invalidRules.length === 0;

        if (isInitialState) {
            field.removeAttribute('data-state');

            return;
        }

        const { value } = field.querySelector('input');
        if (value !== validationReport.value) {
            // Skip validation report processing if the user has changed the value in the meantime
            return;
        }

        if (isValid) {
            const event = new CustomEvent('isValid', { detail: validationReport });

            field.dispatchEvent(event);
            field.setAttribute('data-state', 'success');
            validator.removeMessages(field, 'error');
            validator.removeMessages(field, 'warning');

            if (validationConfig.mode === 'async' && validationConfig.messages && validationReport.httpStatus) {
                const message = validationConfig.messages[validationReport.httpStatus];

                if (message) {
                    const messageNode = validator.createMessage('warning', message);

                    messages.appendChild(messageNode);
                    field.setAttribute('data-state', 'warning');
                }
            } else if (messages.querySelector('.input-message--remark') === null) {
                messages.classList.remove('input-messages--show');
            }
        } else {
            messages.innerHTML = '';

            const invalidRule = validationReport.invalidRules[0];

            if (invalidRule) {
                const messageNode = validator.createMessage('error', invalidRule.message);

                messageNode.setAttribute('data-rule-ref', invalidRule.pattern);
                messages.appendChild(messageNode);
            }

            const event = new CustomEvent('isInvalid', { detail: validationReport });

            field.dispatchEvent(event);

            field.setAttribute('data-state', 'error');
        }
    },

    createMessage: (type, text) => {
        const messageNode = document.createElement('li');

        messageNode.innerText = text;
        messageNode.classList.add('input-message');
        messageNode.classList.add(`input-message--${type}`);

        return messageNode;
    },

    removeMessages: (field, messageType) => {
        const messages = field.querySelector('.input-messages');
        const errorMessages = messages.querySelectorAll(`.input-message--${messageType}`);

        errorMessages.forEach(x => messages.removeChild(x));
    },

    /**
     * Validate value of field against give rules
     * @param field
     * @param rules
     * @param validationMode: serial or parallel.
     *                        serial -> stop validation on invalidation against rule.
     *                        parallel -> continue validation after invalidation against remaining rules
     * @return Validation report object: {{isValid: boolean, invalidRules: Array, validRules: Array}}
     */
    validateFieldAsync: async field => {
        const validationConfig = JSON.parse(field.getAttribute('data-validation-config'));
        const validationMode = validationConfig.validationMode || 'serial';
        const { rules } = validationConfig;
        const { value } = field.querySelector('input');
        const report = validator.createReportObject(field, validationConfig, validationMode, value);
        const isRequired = validator.isRequired(rules);

        if (!isRequired && value.length === 0) {
            validator.processFieldReport(field, report);

            return report;
        }

        for (let i = 0; i < rules.length; i++) {
            const rule = rules[i];

            if (!validator.ruleIsDisabled(rule)) {
                let valueIsValid = true;

                if (rule.pattern === 'service') {
                    /* eslint-disable-next-line no-await-in-loop */
                    const response = await validator.validateValueByService(
                        rule.call.replace('{{value}}', value),
                        validationConfig
                    );

                    valueIsValid = response.isValid;
                    report.httpStatus = response.status;
                    rule.message = response.message;

                    if (response.suggestions) {
                        report.suggestions = response.suggestions;
                    }
                } else {
                    valueIsValid = validator.validateValue(value, rule.pattern);
                }

                if (!valueIsValid && !validator.ruleIsOptional(rule)) {
                    report.invalidRules.push(rule);

                    if (validationMode === 'serial') {
                        break;
                    }
                } else {
                    report.validRules.push(rule);
                }
            }
        }

        if (report.invalidRules.length > 0) {
            report.isValid = false;
        }

        validator.processFieldReport(field, report);

        return report;
    },

    validateField: field => {
        const validationConfig = JSON.parse(field.getAttribute('data-validation-config'));
        const validationMode = validationConfig.validationMode || 'serial';
        const { rules } = validationConfig;
        const { value } = field.querySelector('input');
        const report = validator.createReportObject(field, validationConfig, validationMode, value);
        const isRequired = validator.isRequired(rules);

        if (!isRequired && value.trim().length === 0) {
            validator.processFieldReport(field, report);

            return report;
        }

        for (let i = 0; i < rules.length; i++) {
            const rule = rules[i];

            if (rule.pattern === 'service') {
                throw new Error('Service validation cannot be done synchronous');
            }

            if (!validator.ruleIsDisabled(rule)) {
                let valueIsValid = true;

                valueIsValid = validator.validateValue(value, rule.pattern);

                if (!valueIsValid && !validator.ruleIsOptional(rule)) {
                    report.invalidRules.push(rule);

                    if (validationMode === 'serial') {
                        break;
                    }
                } else {
                    report.validRules.push(rule);
                }
            }
        }

        if (report.invalidRules.length > 0) {
            report.isValid = false;
        }

        validator.processFieldReport(field, report);

        return report;
    },

    validateValue: (value, pattern) => {
        const patternValue = patterns[pattern] || pattern;

        return patternValue.test(value);
    },

    validateValueByService: (url, validationConfig = {}) =>
        new Promise(resolve => {
            const response = {
                isValid: true,
                message: ''
            };

            const { messages } = validationConfig;

            const xhr = new XMLHttpRequest();

            xhr.open('GET', url, true);
            xhr.send();

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 400) {
                    const result = parseJSON(xhr.responseText);
                    response.isValid = true;

                    if (result) {
                        response.suggestions = result.suggestions;
                    }
                } else {
                    response.isValid = false;

                    if (messages && messages[xhr.status]) {
                        response.message = messages[xhr.status];
                    } else if (messages && messages.error) {
                        response.message = messages.error;
                    } else {
                        response.message = 'Oeps, er is een serverfout opgetreden!';
                    }
                }

                response.status = xhr.status;

                resolve(response);
            };
        }),

    isRequired: rules => {
        const isRequired = rules.some(rule => {
            return rule.pattern === 'required' && (typeof rule.disabled === 'undefined' || rule.disabled === false);
        });

        return isRequired;
    },

    ruleIsOptional: rule => typeof rule.optional !== 'undefined' && rule.optional === true,
    ruleIsDisabled: rule => typeof rule.disabled !== 'undefined' && rule.disabled === true,

    createReportObject: (field, validationConfig, validationMode, value) => ({
        field,
        validationConfig,
        validationMode,
        value,
        isValid: true,
        invalidRules: [],
        validRules: []
    }),

    toggleValidationRule: (field, rule, turnOn) => {
        const validationConfig = JSON.parse(field.getAttribute('data-validation-config'));

        validationConfig.rules.forEach(validationRule => {
            if (validationRule.pattern === rule) {
                validationRule.disabled = !turnOn;
            }
        });
        field.setAttribute('data-validation-config', JSON.stringify(validationConfig));
    }
};

Object.freeze(validator);

export default validator;
